import React, { useEffect, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";

function FinanceInvoiceReversePaymentModal({
  show = false,
  onClose,
  onSave,
  saving = false,
  currency = "AUD",
  invoiceNumber = "",
  paymentRecord = null,
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const paymentAmount = Math.abs(Number(paymentRecord?.amount) || 0);

  useEffect(() => {
    if (!show) return;

    setReason("");
    setError("");
  }, [show, paymentRecord?.id]);

  const canSave = useMemo(
    () => !saving && paymentRecord?.id && String(reason || "").trim(),
    [saving, paymentRecord, reason],
  );

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    const normalizedReason = String(reason || "").trim();

    if (!normalizedReason) {
      setError("Reason is required.");
      return;
    }

    const saved = await onSave?.(
      {
        transaction_id: paymentRecord.id,
        amount: paymentAmount,
        reason: normalizedReason,
      },
      paymentRecord,
    );

    if (saved) {
      onClose?.();
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="invoice-finance-payment-modal__backdrop"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className="invoice-finance-payment-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-finance-reverse-payment-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="invoice-finance-payment-modal__header">
          <div>
            <h4
              id="invoice-finance-reverse-payment-modal-title"
              className="invoice-finance-payment-modal__title mb-0"
            >
              Reverse payment
            </h4>
            {invoiceNumber ? (
              <p className="invoice-finance-payment-modal__subtitle mb-0">
                Invoice {invoiceNumber}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="invoice-finance-payment-modal__close"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className="invoice-finance-payment-modal__body">
          <div className="invoice-finance-payment-modal__summary">
            <span>Payment amount</span>
            <strong>
              {currency}{" "}
              {paymentAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          </div>

          <div className="invoice-finance-payment-modal__field">
            <label htmlFor="finance-invoice-reverse-reason">
              Reason for reversal <span className="text-danger">*</span>
            </label>
            <textarea
              id="finance-invoice-reverse-reason"
              className="form-control"
              rows={4}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setError("");
              }}
              placeholder="Why is this payment being reversed?"
              disabled={saving}
            />
          </div>

          {error ? (
            <div className="invoice-finance-payment-modal__error">{error}</div>
          ) : null}
        </div>

        <div className="invoice-finance-payment-modal__footer">
          <button
            type="button"
            className="invoice-finance-payment-modal__btn invoice-finance-payment-modal__btn--cancel"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="invoice-finance-payment-modal__btn invoice-finance-payment-modal__btn--save"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? "Reversing…" : "Reverse payment"}
          </button>
        </div>
      </div>
    </>
  );
}

export default FinanceInvoiceReversePaymentModal;
